import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUser } from '../auth/useAuth';
import { useApi } from '../api/client';
import type { GameId } from '../api/types';
import { track } from '../services/analyticsService';
import { withLocalTicketPreviewMocks } from '../utils/localTicketPreview';
import {
  dedupeKey,
  genId,
  safeParseStoredTickets,
  ticketMatchesGame,
  type ScannedTicket,
  type TicketSource,
} from './scannedTicketUtils';

export type { ScannedTicket, TicketSource };

const STORAGE_KEY = 'chancey_tickets';

function readLocal(): ScannedTicket[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return safeParseStoredTickets(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeLocal(tickets: ScannedTicket[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tickets));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

function sameTickets(a: ScannedTicket[], b: ScannedTicket[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export type NewTicketInput = Omit<ScannedTicket, 'id' | 'scannedAt'> & {
  id?: string;
  scannedAt?: string;
};

export interface AddTicketResult {
  id: string;
  ticket: ScannedTicket;
  deduped: boolean;
}

export interface UseScannedTicketsValue {
  tickets: ScannedTicket[];
  addTicket: (input: NewTicketInput) => Promise<AddTicketResult>;
  updateTicket: (id: string, patch: Partial<Omit<ScannedTicket, 'id' | 'scannedAt'>>) => Promise<void>;
  removeTicket: (id: string) => Promise<void>;
}

export interface UseScannedTicketsForGameValue extends UseScannedTicketsValue {
  /** Real saved tickets for the selected game. Never includes local preview tickets. */
  gameTickets: ScannedTicket[];
  /** Game tickets plus local-only preview rows for visual testing in development. */
  displayTickets: ScannedTicket[];
}

const Ctx = createContext<UseScannedTicketsValue | null>(null);

export const ScannedTicketsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isLoaded } = useUser();
  const api = useApi();
  const [tickets, setTickets] = useState<ScannedTicket[]>(() => readLocal());
  const ticketsRef = useRef(tickets);
  const confirmedTicketsRef = useRef(tickets);
  const confirmedRemoteForUserId = useRef<string | null>(null);
  const persistChain = useRef<Promise<void>>(Promise.resolve());
  const hydratedRemoteForUserId = useRef<string | null>(null);

  const mirrorConfirmed = useCallback((nextConfirmed: ScannedTicket[]) => {
    confirmedTicketsRef.current = nextConfirmed;
    writeLocal(nextConfirmed);
  }, []);

  const enqueueSignedInPersist = useCallback(
    (operation: () => Promise<ScannedTicket[]>) => {
      persistChain.current = persistChain.current
        .then(operation)
        .then((nextConfirmed) => {
          mirrorConfirmed(nextConfirmed);
        })
        .catch(() => {
          /* D1 remains source of truth; do not mirror failed signed-in writes locally. */
        });
      return persistChain.current;
    },
    [mirrorConfirmed]
  );

  const persistAddTicket = useCallback(
    async (ticket: ScannedTicket) => {
      if (!user) {
        mirrorConfirmed(ticketsRef.current);
        return;
      }
      void enqueueSignedInPersist(async () => {
        await api.post('/v1/me/tickets', { ticket });
        const confirmed = confirmedTicketsRef.current;
        return [ticket, ...confirmed.filter((existing) => existing.id !== ticket.id)];
      });
    },
    [api, enqueueSignedInPersist, mirrorConfirmed, user]
  );

  const persistUpdateTicket = useCallback(
    async (id: string, patch: Partial<Omit<ScannedTicket, 'id' | 'scannedAt'>>) => {
      if (!user) {
        mirrorConfirmed(ticketsRef.current);
        return;
      }
      void enqueueSignedInPersist(async () => {
        await api.patch(`/v1/me/tickets/${encodeURIComponent(id)}`, patch);
        return confirmedTicketsRef.current.map((ticket) =>
          ticket.id === id ? { ...ticket, ...patch } : ticket
        );
      });
    },
    [api, enqueueSignedInPersist, mirrorConfirmed, user]
  );

  const persistRemoveTicket = useCallback(
    async (id: string) => {
      if (!user) {
        mirrorConfirmed(ticketsRef.current);
        return;
      }
      void enqueueSignedInPersist(async () => {
        await api.delete(`/v1/me/tickets/${encodeURIComponent(id)}`);
        return confirmedTicketsRef.current.filter((ticket) => ticket.id !== id);
      });
    },
    [api, enqueueSignedInPersist, mirrorConfirmed, user]
  );

  useEffect(() => {
    ticketsRef.current = tickets;
  }, [tickets]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      hydratedRemoteForUserId.current = null;
      confirmedRemoteForUserId.current = null;
      confirmedTicketsRef.current = ticketsRef.current;
      return;
    }
    if (hydratedRemoteForUserId.current === user.id) return;
    hydratedRemoteForUserId.current = user.id;
    confirmedRemoteForUserId.current = null;
    confirmedTicketsRef.current = [];
    const cacheAtRequestStart = ticketsRef.current;
    api
      .get<{ tickets?: unknown[] }>('/v1/me/tickets')
      .then((body) => {
        if (!sameTickets(ticketsRef.current, cacheAtRequestStart)) return;
        const remote = safeParseStoredTickets(body.tickets);
        if (!sameTickets(ticketsRef.current, remote)) {
          ticketsRef.current = remote;
          setTickets(remote);
        }
        mirrorConfirmed(remote);
        confirmedRemoteForUserId.current = user.id;
      })
      .catch(() => {
        /* keep current in-memory state; do not refresh signed-in cache from a failed D1 read */
      });
  }, [api, isLoaded, mirrorConfirmed, user]);

  const schedulePersist = useCallback(
    (next: ScannedTicket[]) => {
      if (!user) mirrorConfirmed(next);
    },
    [mirrorConfirmed, user]
  );

  const addTicket = useCallback<UseScannedTicketsValue['addTicket']>(
    async (input) => {
      const prev =
        user && confirmedRemoteForUserId.current !== user.id
          ? confirmedTicketsRef.current
          : ticketsRef.current;
      const key = dedupeKey(input.main, input.bonus, input.drawDate, input.gameId);
      const match = prev.find(
        (t) => dedupeKey(t.main, t.bonus, t.drawDate, t.gameId) === key
      );
      if (match) {
        return { id: match.id, ticket: match, deduped: true };
      }

      const id = input.id ?? genId();
      const scannedAt = input.scannedAt ?? new Date().toISOString();
      const ticket: ScannedTicket = { ...input, id, scannedAt };
      const next = [ticket, ...prev];
      ticketsRef.current = next;
      setTickets(next);
      schedulePersist(next);
      void persistAddTicket(ticket);
      track('ticket_save', {
        gameId: ticket.gameId,
        source: ticket.source,
        outcome: ticket.status,
      });
      return { id, ticket, deduped: false };
    },
    [persistAddTicket, schedulePersist, user]
  );

  const removeTicket = useCallback<UseScannedTicketsValue['removeTicket']>(
    async (id) => {
      const next = ticketsRef.current.filter((t) => t.id !== id);
      ticketsRef.current = next;
      setTickets(next);
      schedulePersist(next);
      void persistRemoveTicket(id);
      track('ticket_delete');
    },
    [persistRemoveTicket, schedulePersist]
  );

  const updateTicket = useCallback<UseScannedTicketsValue['updateTicket']>(
    async (id, patch) => {
      const prev = ticketsRef.current;
      const index = prev.findIndex((t) => t.id === id);
      if (index === -1) return;
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      ticketsRef.current = next;
      setTickets(next);
      schedulePersist(next);
      void persistUpdateTicket(id, patch);
    },
    [persistUpdateTicket, schedulePersist]
  );

  const value = useMemo<UseScannedTicketsValue>(
    () => ({
      tickets,
      addTicket,
      updateTicket,
      removeTicket,
    }),
    [tickets, addTicket, updateTicket, removeTicket]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useScannedTickets(): UseScannedTicketsValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useScannedTickets must be used within ScannedTicketsProvider');
  return v;
}

export function useScannedTicketsForGame(gameId: GameId): UseScannedTicketsForGameValue {
  const scannedTickets = useScannedTickets();
  const gameTickets = useMemo(
    () => scannedTickets.tickets.filter((ticket) => ticketMatchesGame(ticket, gameId)),
    [gameId, scannedTickets.tickets]
  );
  const displayTickets = useMemo(
    () => withLocalTicketPreviewMocks(gameTickets, gameId),
    [gameId, gameTickets]
  );

  return useMemo(
    () => ({
      ...scannedTickets,
      gameTickets,
      displayTickets,
    }),
    [displayTickets, gameTickets, scannedTickets]
  );
}
